import axios from 'axios';
import * as yaml from 'yaml';

interface RepositoryFileResult {
  content: string | null;
  errors: string[] | null;
}

interface KeyPairAuthCheckResult {
  isConfigured: boolean;
  errors: string[] | null;
}

/**
 * Fetches a file from a GitLab repository
 * @param platformUrl - The base URL of the GitLab platform
 * @param token - The OAuth token for authentication
 * @param repoId - The repository ID
 * @param filePath - The path to the file in the repository
 * @param ref - The branch/tag reference (default: 'main')
 * @returns Promise containing the file content or errors
 */
export async function getRepositoryFile(
  platformUrl: string,
  token: string,
  repoId: number,
  filePath: string,
  ref: string = 'main',
): Promise<RepositoryFileResult> {
  try {
    const encodedFilePath = encodeURIComponent(filePath);
    const url = `${platformUrl}api/v4/projects/${repoId}/repository/files/${encodedFilePath}/raw?ref=${ref}`;

    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    console.log(`Fetched file ${filePath} from repository ${repoId} successfully`);
    return {
      content: response.data,
      errors: null,
    };
  } catch (error: any) {
    const errorMessage = `Error while fetching file ${filePath} from repository ${repoId}`;
    console.warn(errorMessage, error);

    return {
      content: null,
      errors: [errorMessage, error.response?.data || error.message],
    };
  }
}

/**
 * Checks if a solution is configured for key pair authentication
 * This is determined by the presence of DATAOPS_SNOWFLAKE_AUTH: "KEY_PAIR" in configuration files
 * @param platformUrl - The base URL of the GitLab platform
 * @param oauthToken - The OAuth token for authentication
 * @param repoId - The repository ID
 * @param ref - The branch/tag reference (default: 'main')
 * @returns Promise containing whether key pair auth is configured
 */
export async function checkIfSolutionIsConfiguredForKeyPairAuth(
  platformUrl: string,
  oauthToken: string,
  repoId: number,
  ref: string = 'main',
): Promise<KeyPairAuthCheckResult> {
  const defaultPossibleFilePaths = [
    'dataops/pipelines/includes/config/variables.yml',
    'pipelines/includes/config/variables.yml',
  ];

  // Get possible file paths from environment variable if available
  const envVarPaths = process.env.POSSIBLE_VARIABLE_FILE_PATHS;
  let possibleFilePaths: string[] = [];

  if (envVarPaths) {
    possibleFilePaths = envVarPaths.split(',');
  }

  possibleFilePaths.push(...defaultPossibleFilePaths);

  // Try each path until we find a valid file
  for (const filePath of possibleFilePaths) {
    try {
      const result = await getRepositoryFile(platformUrl, oauthToken, repoId, filePath, ref);

      if (!result.errors && result.content) {
        // File found, check for key pair authentication
        try {
          const variablesYaml = yaml.parse(result.content) as any;
          const variables = variablesYaml?.variables;

          if (!variables) {
            continue; // Try next path
          }

          const authMethod = variables.DATAOPS_SNOWFLAKE_AUTH;
          if (!authMethod) {
            continue; // Try next path
          }

          if (authMethod === 'KEY_PAIR') {
            console.log(`Key pair authentication is configured in ${filePath}`);
            return {
              isConfigured: true,
              errors: null,
            };
          }
          // If auth_method is not KEY_PAIR, try next path
        } catch (yamlError) {
          console.warn(`Failed to parse YAML from ${filePath}:`, yamlError);
          continue; // Try next path
        }
      }
    } catch (error) {
      console.warn(`Failed to fetch ${filePath}:`, error);
      continue; // Try next path
    }
  }

  // If we've tried all paths and none worked, return false
  return {
    isConfigured: false,
    errors: null,
  };
}
